import React, { useState } from "react";
import { X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import backendApi from "@/api/backendApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function KeySkillsSection({ profile }) {
  const queryClient = useQueryClient();
  const userSkills = (profile.skills || []).filter(s => s !== null); // Linked to 'skills' column in DB and filtered for safety

  const [isEditing, setIsEditing] = useState(false);
  const [newSkill, setNewSkill] = useState("");

  // MUTATIONS
  const addMutation = useMutation({
    mutationFn: (skillName) => backendApi.entities.UserProfile.addSkill({ skill: skillName }),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
      setNewSkill("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => backendApi.entities.UserProfile.deleteSkill(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
    },
  });

  const handleAddSkill = async () => {
    const skill = newSkill.trim();
    if (!skill) return;
    try {
      await addMutation.mutateAsync(skill);
      // Force immediate cache invalidation and UI sync
      queryClient.invalidateQueries(["userProfile"]);
    } catch (err) {
      console.error("Add skill failed:", err);
    }
  };

  const handleRemoveSkill = async (id) => {
    try {
      await deleteMutation.mutateAsync(id);
      queryClient.invalidateQueries(["userProfile"]);
    } catch (err) {
      console.error("Delete skill failed:", err);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Key skills</h3>
        <button
          onClick={() => setIsEditing(true)}
          className="text-blue-600 hover:underline text-sm font-medium"
        >
          {(profile?.skills && profile.skills.length > 0) ? "Edit" : "Add"}
        </button>
      </div>

      {/* Skills Display */}
      <div className="flex flex-wrap gap-2">
        {userSkills.length > 0 ? (
          userSkills.map((skill, idx) => (
            <span
              key={skill.id || `skill-${idx}`}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm"
            >
              {typeof skill === "string" ? skill : (skill.name || skill.skillName || JSON.stringify(skill))}
            </span>
          ))
        ) : (
          <p className="text-gray-500">Add skills to improve your profile.</p>
        )}
      </div>

      {/* EDIT DIALOG */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Key Skills</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Add Skill Input */}
            <div className="flex gap-2">
              <Input
                placeholder="Add a skill"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddSkill()}
              />
              <Button onClick={handleAddSkill} disabled={addMutation.isPending}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Editable Skills */}
            <div className="flex flex-wrap gap-3">
              {userSkills.map((skill, idx) => (
                <span
                  key={skill.id || `edit-skill-${idx}`}
                  className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-2 group shadow-sm"
                >
                  <span className="font-medium">
                    {typeof skill === "string" ? skill : (skill.name || skill.skillName)}
                  </span>
                  <button 
                    onClick={() => handleRemoveSkill(skill.id || skill)} 
                    disabled={deleteMutation.isPending}
                    className="hover:text-red-500 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
